// @ts-nocheck
import { useMemo } from 'react'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { InsightCategory } from 'schemas/action/InsightCategorySchema'
import { WidgetId } from 'features/types'
import { DashboardWidget } from 'features/widget/types'
import { isMeasurementType } from 'utils/viewOptionUtil'
import ViewOption from 'enums/InsightViewOption'
import { SideBySideResult } from 'schemas/SideBySideResultSchema'
import { SideBySideWidgetsType, SideBySideData } from '../../types'

const reorderResultsAsHeader = (
  insightCountResultArr: SideBySideResult['results'],
  widgetSetting: SideBySideWidgetsType
) => {
  const { pivotThemeInfos, tagNames, docNames } = widgetSetting
  const themeLength = pivotThemeInfos?.length ?? 0
  const tagLength = tagNames?.length ?? 0
  const docLength = docNames?.length ?? 0

  if (themeLength > 0) {
    return pivotThemeInfos?.map((theme) =>
      insightCountResultArr.find(
        (insightCountResult) => insightCountResult.themeId === `${theme.themeId}`
      )
    )
  }
  if (tagLength > 0) {
    return tagNames?.map((tagName) =>
      insightCountResultArr.find((insightCountResult) => insightCountResult.tagName === tagName)
    )
  }
  if (docLength > 0) {
    return docNames?.map((docName) =>
      insightCountResultArr.find((insightCountResult) => insightCountResult.docName === docName)
    )
  }

  return insightCountResultArr
}

const reorderApiResultsAsTopicInfo = (
  results: DashboardWidget<SideBySideResult>['data'][number]['results'],
  topicInfos: SideBySideWidgetsType['topicInfos']
) =>
  topicInfos.map((topicInfo) =>
    results.find((result) => result.service.parameters.topicIds[0] === topicInfo.topicId)
  )

const hasMultipleDataSet = (viewOption: SideBySideWidgetsType['viewOption']) =>
  [ViewOption.ATTRIBUTES, ViewOption.EMOTIONS, ViewOption.BEHAVIORS].includes(viewOption)

const getFlattenDataSet = (dataSets: InsightCategory) =>
  dataSets.reduce(
    (acc, dataSet) => {
      const { set, status, insightType } = dataSet
      const setWithInsightType = set?.map((s) => ({ ...s, insightType }))
      return {
        set: acc.set?.concat(setWithInsightType).sort((a, b) => b.value - a.value),
        status,
        insightType: acc.insightType + insightType
      }
    },
    {
      set: [],
      status: '',
      insightType: ''
    }
  )

const transformedSideBySideData = (
  widgetData: DashboardWidget<SideBySideResult>,
  setting: SideBySideWidgetsType
) => {
  if (!widgetData) return widgetData

  const { data } = widgetData
  if (!data) return data

  const { minCount, topN, viewOption, topicInfos, pivotThemeInfos } = setting

  const isViewMeasurementType = isMeasurementType(viewOption)

  let isDataChanged = false
  let isAllEmpty = true
  const simplifiedData = []
  const allStatus = []

  const { results } = data[0]

  // * reorder api results array to be same as topicInfo
  const reorderedApiResults = reorderApiResultsAsTopicInfo(results, topicInfos)

  reorderedApiResults.forEach((result, idx) => {
    simplifiedData.push({
      datas: []
    })

    // NI-2615: reorderApiResultsAsTopicInfo returns undefined for topics absent
    // from this runSearches response. Skip the cell so later render paths see
    // an empty `datas` array rather than dereferencing `.data.results`.
    if (!result?.data?.results) return

    // * reorder results array to be same as header column
    const reorderedResults = reorderResultsAsHeader(result.data.results, setting)

    reorderedResults.forEach((insightCountResult) => {
      // NI-2615: Prospero may omit `insights` (or return an empty dataset) for
      // a (topic x category x sentiment) triple — e.g. ポジネガ別キーワード
      // fires two InsightCount calls (additionalSentiments=Positive, =Negative)
      // and one half can land here with `insights: []`. Treat an unresolvable
      // dataset chain as an empty cell so SideBySideEmptyResultGuard can render
      // the friendly state instead of the raw TypeError bubbling through the
      // outer ErrorBoundary.
      const dataSets = insightCountResult?.insightCount?.insights?.[0]?.dataset
      let transformedDataSet

      if (!dataSets || dataSets.length === 0) {
        transformedDataSet = { set: [], status: 'OK', insightType: '' }
      } else if (hasMultipleDataSet(viewOption)) {
        // * 0. flatten dataSets in these viewOptions: ATTRIBUTES EMOTIONS BEHAVIORS
        transformedDataSet = getFlattenDataSet(dataSets)
      } else {
        const { set, insightType } = dataSets[0]
        transformedDataSet = {
          ...dataSets[0],
          set: [...(set?.map((s) => ({ ...s, insightType })) ?? [])]
        }
      }

      const { set, status, insightType } = transformedDataSet
      let transformedSet = set

      // * 1. remain the length of topN if not Voc
      if (!isViewMeasurementType) transformedSet?.splice(topN)

      // * 2. remove low count
      let isSetChanged = false
      if (minCount && !isViewMeasurementType) {
        const filteredSet = transformedSet.filter((insight) => insight.value > minCount)
        isSetChanged = !(transformedSet.length === filteredSet.length)
        transformedSet = filteredSet
      }

      // * 3. check is set empty
      const isSetEmpty = transformedSet.every((insight) => insight.value <= 0)

      // * 4. check is all data empty
      isAllEmpty = isAllEmpty && isSetEmpty

      // * 5. check is set changed before
      if (!isDataChanged && isSetChanged) {
        isDataChanged = true
      }

      // * 6. collect all status
      allStatus.push(status)

      // * 7. produce an simplified data format
      const { topicId, name: topicName } = topicInfos[idx]
      const themeId = insightCountResult.themeId ? Number(insightCountResult.themeId) : undefined
      const themeName = themeId
        ? pivotThemeInfos.find((themeInfo) => themeInfo.themeId === themeId)?.name
        : undefined

      simplifiedData[idx] = {
        datas: [
          ...simplifiedData[idx].datas,
          {
            set: transformedSet,
            isSetEmpty,
            topicId,
            topicName,
            themeId,
            themeName,
            tagName: insightCountResult.tagName,
            docName: insightCountResult.docName,
            status,
            isSetChanged,
            insightType,
            error: result?.error
          }
        ],
        topicName
      }
    })
  })

  return {
    simplifiedData,
    isAllEmpty,
    isDataChanged,
    allStatus
  }
}

const useSideBySideData = (widgetId: WidgetId): SideBySideData => {
  const widgetData = useDashboardWidgetData<SideBySideResult>(widgetId)
  const widgetSetting = useDashboardWidgetSetting<SideBySideWidgetsType>(widgetId)

  const sideBySideData = useMemo(() => transformedSideBySideData(widgetData, widgetSetting), [
    widgetData,
    widgetSetting
  ])

  return sideBySideData
}

export default useSideBySideData
